import { addNewLineItem } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext, { IEntity } from "@odata/BindingContext";
import {
    EntitySetName,
    IPrTaxDiscountTypeEntity,
    IPrTaxpayerDeclarationDiscountEntity,
    IPrTaxpayerDeclarationEntity,
    PrTaxpayerDeclarationChildEntity,
    PrTaxpayerDeclarationEntity
} from "@odata/GeneratedEntityTypes";
import {
    LanguageCode,
    PrChildDeductionOrderCode,
    PrTaxDiscountTypeCode,
    PrTaxpayerDeclarationTypeCode
} from "@odata/GeneratedEnums";
import { ITaxPayerDeclarationCustomData } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclarationFormView";
import FileStorage from "@utils/FileStorage";
import { isDefined, isObjectEmpty } from "@utils/general";
import saveAs from "file-saver";

import { REST_API_URL } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { FieldType } from "../../../enums";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import { IDeductionTypePaths } from "./TaxpayerDeclaration.types";

export const getTogglePropPath = (type: PrTaxDiscountTypeCode): string => {
    return `##Show${type}##`;
};

export const showChildrenPath = BindingContext.localContext(`Show${PrTaxpayerDeclarationEntity.Children}`);
export const isThereOtherProviderPath = "##isThereOtherProvider##";
export const hasDisabilityDiscount = "##hasDisabilityDiscount##";
export const disabilityDiscountType = "##disabilityDiscountType##";

export const getPathsForDeductionType = (type: PrTaxDiscountTypeCode): IDeductionTypePaths => {
    return {
        switchPath: getTogglePropPath(type)
    };
};

export async function massExportToPDF(ids: string[], context: IAppContext, languageCode = LanguageCode.Czech): Promise<void> {
    const queryIds = `taxpayerDeclarationIds=${ids.join("&taxpayerDeclarationIds=")}`;
    const pdfUrl = `${REST_API_URL}/PrTaxpayerDeclarationsPdfExport?languageCode=${languageCode}&${queryIds}&CompanyId=${context.getCompanyId()}`;

    const file = await FileStorage.get(null, {
        url: pdfUrl
    });

    saveAs(file);
}

const createDiscountsFromLocalContext = (storage: FormStorage, path: string): IPrTaxpayerDeclarationDiscountEntity[] => {
    const discounts: IPrTaxpayerDeclarationDiscountEntity[] = [];

    Object.values(PrTaxDiscountTypeCode).filter(type => {
        // Exclude disability discounts as they are handled separately
        return type !== PrTaxDiscountTypeCode.BasicDisabilityDiscount && type !== PrTaxDiscountTypeCode.ExtendedDisabilityDiscount;
    }).forEach(type => {
        const { switchPath } = getPathsForDeductionType(type);

        if (storage.getValueByPath(switchPath)) {
            const discount = {
                DiscountTypeCode: type
            } as IPrTaxpayerDeclarationDiscountEntity;

            discounts.push(discount);
        }
    });

    if (storage.getValueByPath(hasDisabilityDiscount)) {
        const discountType = storage.getValueByPath(disabilityDiscountType);
        const discount = {
            DiscountTypeCode: discountType
        } as IPrTaxpayerDeclarationDiscountEntity;

        discounts.push(discount);
    }

    let origEntity = storage.data.origEntity as IEntity;
    if (isDefined(path)) {
        origEntity = origEntity[path];
    }

    origEntity?.Discounts?.forEach((origDiscount: any) => {
        const matchingDiscount = discounts.find(discount => discount.DiscountTypeCode === origDiscount.DiscountTypeCode);
        if (matchingDiscount) {
            matchingDiscount.Id = origDiscount.Id;
        }
    });

    return discounts;
};

export function taxpayerDeclarationOnAfterLoad(entity: IPrTaxpayerDeclarationEntity, storage: FormStorage): void {
    const basicDiscount = entity.Discounts?.find(d => d.DiscountTypeCode === PrTaxDiscountTypeCode.BasicDisabilityDiscount);
    const extendedDiscount = entity.Discounts?.find(d => d.DiscountTypeCode === PrTaxDiscountTypeCode.ExtendedDisabilityDiscount);
    if (!isObjectEmpty(basicDiscount) || !isObjectEmpty(extendedDiscount)) {
        storage.setValueByPath(hasDisabilityDiscount, true);
        storage.setValueByPath(disabilityDiscountType, !isObjectEmpty(basicDiscount) ? PrTaxDiscountTypeCode.BasicDisabilityDiscount : PrTaxDiscountTypeCode.ExtendedDisabilityDiscount);
    }

    storage.setValueByPath(isThereOtherProviderPath, !!entity.OtherProvider.PersonalIdentificationNumber);

    entity.Discounts?.forEach(discount => {
        const {
            switchPath
        } = getPathsForDeductionType(discount.DiscountTypeCode as PrTaxDiscountTypeCode);

        storage.setValueByPath(switchPath, true);
    });

    const hasChildren = !!entity.Children?.length;
    storage.setValueByPath(showChildrenPath, hasChildren);
}

export function taxpayerDeclarationOnBeforeSave(entity: IPrTaxpayerDeclarationEntity, storage: FormStorage, prefix = ""): IPrTaxpayerDeclarationEntity {
    entity.Discounts = createDiscountsFromLocalContext(storage, prefix);

    if (!storage.getValueByPath(isThereOtherProviderPath)) {
        entity.OtherProvider = null;
    }

    if (!entity.OtherProvider?.IsEmployed && !isObjectEmpty(entity.OtherProvider)) {
        delete entity.OtherProvider?.EmployerAddress;
    }

    if (isDefined(entity.DateApplyFrom)) {
        entity.Year = getUtcDayjs(entity.DateApplyFrom).get("year");
    }

    if (storage.data.bindingContext.isNew()) {
        entity.TypeCode = PrTaxpayerDeclarationTypeCode.Regular;
    }
    return entity;
}

export async function loadPrTaxDiscountTypes(storage: FormStorage<IEntity, ITaxPayerDeclarationCustomData>): Promise<void> {
    const res = await storage.oData.getEntitySetWrapper(EntitySetName.PrTaxDiscountTypes)
        .query()
        .fetchData<IPrTaxDiscountTypeEntity[]>();
    const discountTypes: Record<string, IPrTaxDiscountTypeEntity> = {};

    res.value?.forEach((discountType: IPrTaxDiscountTypeEntity) => {
        discountTypes[discountType.Code] = discountType;
    });

    storage.setCustomData({
        discountTypes
    });

    storage.refresh();
}

export function commonTaxpayerDeclarationOnChange(e: ISmartFieldChange, storage: FormStorage, path?: string): void {
    const childrenPath = isDefined(path) ? `${path}/${PrTaxpayerDeclarationEntity.Children}` : PrTaxpayerDeclarationEntity.Children;
    if (e.bindingContext.getPath() === showChildrenPath) {
        if (!e.value) {
            storage.setValueByPath(childrenPath, []);
        } else {
            addNewLineItem(storage, childrenPath, PrTaxpayerDeclarationEntity.Children, [], {
                [PrTaxpayerDeclarationChildEntity.OrderCode]: PrChildDeductionOrderCode._1stChild,
                [PrTaxpayerDeclarationChildEntity.Order]: {
                    Code: PrChildDeductionOrderCode._1stChild
                },
                [PrTaxpayerDeclarationChildEntity.IsDisabilityDiscount]: false
            });
        }
    }

    if (e.type === FieldType.Switch) {
        storage.refresh();
    } else {
        storage.refreshFields();
    }
}